/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react"
import { useMutation, useQuery } from "@apollo/client/react"
import { useState, type FC } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog"
import { Button } from "../../components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select"
import { useSystemMessages } from "../../hooks/useSystemMessages"
import {
  StartViewAsDocument,
  ViewAsTargetsDocument,
} from "../../services/__generated__/graphql"
import { setViewAs } from "../../services/impersonation"

const s = {
  field: css`
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    margin: 0.5rem 0 1rem;
  `,
  hint: css`
    font-size: 0.75rem;
    color: var(--muted-foreground);
    margin: 0;
  `,
}

/** Pick an LMNT user to view the app as. ROOT-only (the trigger lives in the
 *  account menu, itself ROOT-gated). Starting reloads so the whole app re-renders
 *  as the target — every request then rides the X-Impersonation token. */
export const ViewAsDialog: FC<{
  open: boolean
  onOpenChange: (open: boolean) => void
}> = ({ open, onOpenChange }) => {
  const { addMessage } = useSystemMessages()
  const [email, setEmail] = useState("")
  const { data, loading } = useQuery(ViewAsTargetsDocument, {
    skip: !open,
    fetchPolicy: "cache-and-network",
  })
  const [startViewAs, { loading: starting }] = useMutation(StartViewAsDocument)
  const targets = data?.viewAsTargets ?? []

  const handleStart = async () => {
    if (!email) return
    try {
      const { data } = await startViewAs({ variables: { email } })
      const session = data?.startViewAs
      if (!session?.token) {
        throw new Error("The server did not start a View As session.")
      }
      setViewAs({
        token: session.token,
        targetEmail: session.target.email ?? email,
      })
      // Reload so the whole app re-renders as the target.
      window.location.reload()
    } catch (err) {
      addMessage({
        type: "error",
        title: "Could not start View As",
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) setEmail("")
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>View as user</DialogTitle>
          <DialogDescription>
            See the app exactly as an LMNT user does. You stay signed in as
            yourself; every action is attributed to you and audited.
          </DialogDescription>
        </DialogHeader>

        <div css={s.field}>
          <Select value={email} onValueChange={setEmail}>
            <SelectTrigger>
              <SelectValue
                placeholder={loading ? "Loading users…" : "Select a user"}
              />
            </SelectTrigger>
            <SelectContent>
              {targets
                .filter((t): t is typeof t & { email: string } => !!t.email)
                .map((t) => (
                  <SelectItem key={t.id ?? t.email} value={t.email}>
                    {t.role === "ROOT" ? `${t.email} (Root)` : t.email}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          {!loading && targets.length === 0 && (
            <p css={s.hint}>No LMNT users to view as.</p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={starting}
          >
            Cancel
          </Button>
          <Button onClick={handleStart} disabled={!email || starting}>
            {starting ? "Starting…" : "View as user"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
